/**
 * Countdown for the cart drawer.
 *
 * Two modes:
 *   deadline — counts to a fixed date/time (a real campaign end).
 *   session  — counts a fixed number of minutes from the visitor's first view.
 *
 * The cart drawer re-renders and morphs on every quantity change, which would
 * restart a naive timer each time. The end timestamp is therefore kept in
 * sessionStorage and re-read on connect, so the countdown survives re-renders,
 * drawer close/open and navigation within the session.
 *
 * Classic (non-module) script: the drawer can render more than once per page,
 * so the whole definition is guarded to make a second execution a no-op.
 */
if (!customElements.get('cart-timer')) {
  class CartTimer extends HTMLElement {
    #intervalId = null;

    connectedCallback() {
      this.output = this.querySelector('[data-time]');
      if (!this.output) return;

      this.template = this.dataset.template || '[time]';
      this.endsAt = this.#resolveEnd();

      if (!this.endsAt) {
        this.hidden = true;
        return;
      }

      this.#tick();
      this.#intervalId = setInterval(() => this.#tick(), 1000);
    }

    disconnectedCallback() {
      if (this.#intervalId !== null) {
        clearInterval(this.#intervalId);
        this.#intervalId = null;
      }
    }

    /** @returns {number | null} epoch ms the countdown runs to */
    #resolveEnd() {
      if (this.dataset.mode === 'deadline') {
        // Two shapes are accepted, and both must land on the same instant as
        // the chapter countdown so the store never shows two different clocks:
        //   "YYYY-MM-DD HH:MM" — the space is not ISO 8601, so Safari returns
        //   NaN where Chrome parses it; swapping in a T fixes both.
        //   "YYYY-MM-DD" — a bare date means end of that day, matching
        //   chapter-countdown.js, instead of midnight at its start.
        const raw = (this.dataset.deadline || '').trim();
        const iso = /^\d{4}-\d{2}-\d{2}$/.test(raw)
          ? `${raw}T23:59:59`
          : raw.replace(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/, '$1T$2');
        const parsed = Date.parse(iso);
        return Number.isNaN(parsed) ? null : parsed;
      }

      // Session mode: first view wins, and every later render reuses it.
      const minutes = Number(this.dataset.minutes) || 10;
      const key = 'cart-timer-ends-at';

      let stored = null;
      try {
        stored = sessionStorage.getItem(key);
      } catch {
        // Private mode or storage disabled: fall through to a fresh window.
      }

      const storedEnd = stored ? Number(stored) : NaN;
      if (!Number.isNaN(storedEnd) && storedEnd > Date.now()) return storedEnd;

      const end = Date.now() + minutes * 60 * 1000;
      try {
        sessionStorage.setItem(key, String(end));
      } catch {
        // Nothing to persist to; the timer still runs for this render.
      }
      return end;
    }

    #tick() {
      const remaining = this.endsAt - Date.now();

      if (remaining <= 0) {
        this.hidden = true;
        this.disconnectedCallback();
        return;
      }

      this.output.textContent = this.#format(remaining);
    }

    /** mm:ss under an hour, then h:mm:ss, then Nd h:mm */
    #format(ms) {
      const total = Math.floor(ms / 1000);
      const days = Math.floor(total / 86400);
      const hours = Math.floor((total % 86400) / 3600);
      const minutes = Math.floor((total % 3600) / 60);
      const seconds = total % 60;
      const pad = (n) => String(n).padStart(2, '0');

      if (days > 0) return `${days}d ${hours}:${pad(minutes)}:${pad(seconds)}`;
      if (hours > 0) return `${hours}:${pad(minutes)}:${pad(seconds)}`;
      return `${minutes}:${pad(seconds)}`;
    }
  }

  customElements.define('cart-timer', CartTimer);
}
